/**
 * NmbrsOverridePanel Component
 *
 * Shows a field-by-field comparison between local values and Nmbrs SOAP values.
 * HR/admin can toggle overrides per field to prevent sync from overwriting local values.
 *
 * Story Nmbrs-2.3: Override Beheer per Medewerker
 *
 * Design system: Navy #2F5A7A, Teal #54949E, Green #B7D945, Red #D64545, Border #E2E8F0
 */

import { useState } from 'react';
import {
  useFieldComparison,
  useSetOverride,
  useRemoveOverride,
  type FieldComparison,
} from '../hooks/useNmbrsOverrides';
import { useTriggerEmployeeSync } from '../hooks/useNmbrsSync';

interface NmbrsOverridePanelProps {
  employeeId: number;
}

export function NmbrsOverridePanel({ employeeId }: NmbrsOverridePanelProps) {
  const { data: comparisonData, isLoading, isError, error } = useFieldComparison(employeeId);
  const setOverrideMutation = useSetOverride();
  const removeOverrideMutation = useRemoveOverride();
  const triggerSync = useTriggerEmployeeSync();

  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const fields = comparisonData?.data ?? [];

  const handleToggleOverride = async (field: FieldComparison) => {
    if (field.hasOverride) {
      // Show confirmation dialog before removing (AC7)
      setConfirmRemove(field.fieldName);
    } else {
      // Enable override: use current local value as override value
      const overrideValue = field.localValue || '';
      if (!overrideValue) return;

      try {
        await setOverrideMutation.mutateAsync({
          employeeId,
          fieldName: field.fieldName,
          overrideValue,
        });
      } catch {
        // Error shown via mutation state
      }
    }
  };

  const handleConfirmRemove = async (fieldName: string) => {
    try {
      await removeOverrideMutation.mutateAsync({ employeeId, fieldName });
      setConfirmRemove(null);
    } catch {
      // Error shown via mutation state
    }
  };

  // Don't show anything while loading or if employee is not linked (404)
  if (isLoading) {
    return null;
  }

  if (isError) {
    // If the employee is not linked to Nmbrs, hide the panel entirely
    const errorObj = error as { status?: number } | undefined;
    if (errorObj && 'status' in errorObj && errorObj.status === 404) {
      return null;
    }
    const errorMessage = error instanceof Error ? error.message : 'Kan gegevens niet laden';
    return (
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-[#D64545]">{errorMessage}</p>
        </div>
      </div>
    );
  }

  // No comparison data means not linked
  if (!fields.length) {
    return null;
  }

  return (
    <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#1E293B]">Veld Overrides</h3>
          <p className="text-sm text-gray-500">
            Beheer welke velden worden overschreven door de nachtelijke synchronisatie.
            Een actieve override voorkomt dat de sync de lokale waarde vervangt.
          </p>
        </div>
        {/* Per-employee sync button (Story 2.4 AC8) */}
        <button
          onClick={() => triggerSync.mutate(employeeId)}
          disabled={triggerSync.isPending}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-[#E2E8F0] text-[#2F5A7A] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Synchroniseer deze medewerker nu"
        >
          {triggerSync.isPending ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#2F5A7A]" />
          ) : (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
          Sync
        </button>
      </div>

      {/* Mutation errors */}
      {setOverrideMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-[#D64545]">
            {setOverrideMutation.error instanceof Error
              ? setOverrideMutation.error.message
              : 'Override instellen mislukt'}
          </p>
        </div>
      )}
      {removeOverrideMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-[#D64545]">
            {removeOverrideMutation.error instanceof Error
              ? removeOverrideMutation.error.message
              : 'Override verwijderen mislukt'}
          </p>
        </div>
      )}
      {triggerSync.isSuccess && (
        <div className="bg-[#B7D945]/10 border border-[#B7D945]/30 rounded-lg p-3">
          <p className="text-sm text-[#6B8C1A]">
            Synchronisatie gestart voor deze medewerker.
          </p>
        </div>
      )}

      {/* Field comparison table */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-[#E2E8F0]">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Veld
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Lokale waarde
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Nmbrs waarde
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                Override actief
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Info
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {fields.map((field) => {
              const valuesMatch =
                field.localValue &&
                field.nmbrsValue &&
                field.localValue === field.nmbrsValue;
              const valuesDiffer =
                field.localValue &&
                field.nmbrsValue &&
                field.localValue !== field.nmbrsValue;
              const isPending =
                setOverrideMutation.isPending || removeOverrideMutation.isPending;

              return (
                <tr key={field.fieldName} className="hover:bg-gray-50">
                  {/* Field name */}
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-[#1E293B]">
                      {field.fieldLabel}
                    </span>
                    {(field.fieldName === 'name' || field.fieldName === 'email') && (
                      <span className="ml-2 text-xs text-gray-400" title="Dit veld komt uit het authenticatiesysteem (MySQL) en kan niet automatisch worden bijgewerkt">
                        (auth DB)
                      </span>
                    )}
                  </td>

                  {/* Local value */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-[#1E293B]">
                      {field.localValue || (
                        <span className="text-gray-400 italic">Niet ingesteld</span>
                      )}
                    </span>
                  </td>

                  {/* Nmbrs value */}
                  <td className="px-4 py-3">
                    <span
                      className={`text-sm ${
                        valuesDiffer
                          ? 'text-[#D64545] font-medium'
                          : valuesMatch
                          ? 'text-[#1E293B]'
                          : 'text-gray-400 italic'
                      }`}
                    >
                      {field.nmbrsValue || 'Niet beschikbaar'}
                    </span>
                    {valuesDiffer && !field.hasOverride && (
                      <span className="block text-xs text-gray-400 mt-0.5">
                        Verschilt van lokaal
                      </span>
                    )}
                  </td>

                  {/* Toggle switch (AC6) */}
                  <td className="px-4 py-3 text-center">
                    {confirmRemove === field.fieldName ? (
                      /* Confirmation dialog inline (AC7) */
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleConfirmRemove(field.fieldName)}
                          disabled={removeOverrideMutation.isPending}
                          className="px-2 py-1 text-xs text-white bg-[#D64545] rounded hover:bg-red-600 disabled:opacity-50"
                        >
                          {removeOverrideMutation.isPending ? '...' : 'Bevestigen'}
                        </button>
                        <button
                          onClick={() => setConfirmRemove(null)}
                          className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                        >
                          Annuleren
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleToggleOverride(field)}
                        disabled={isPending || (!field.hasOverride && !field.localValue)}
                        className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#54949E] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          backgroundColor: field.hasOverride ? '#B7D945' : '#E2E8F0',
                        }}
                        title={
                          field.hasOverride
                            ? 'Override actief - klik om uit te schakelen'
                            : field.localValue
                            ? 'Klik om override in te schakelen'
                            : 'Geen lokale waarde om te beveiligen'
                        }
                        aria-label={`Override ${field.fieldLabel} ${field.hasOverride ? 'uitschakelen' : 'inschakelen'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                            field.hasOverride ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    )}
                  </td>

                  {/* Override metadata (AC8) */}
                  <td className="px-4 py-3 text-right">
                    {field.hasOverride && field.overriddenAt && (
                      <div className="text-xs text-gray-400">
                        <div>
                          Ingesteld door #{field.overriddenBy}
                        </div>
                        <div>
                          {new Date(field.overriddenAt).toLocaleDateString('nl-NL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-400">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#B7D945' }} />
          <span>Override actief (sync overschrijft niet)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-[#E2E8F0]" />
          <span>Geen override (sync overschrijft lokale waarde)</span>
        </div>
      </div>
    </div>
  );
}
